import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  recordPaymentSchema,
  SELECTABLE_PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  toCents,
  type RecordPaymentInput,
} from '@jobos/shared';
import { useRecordPayment } from '@/hooks/useInvoicePayments';
import { formatCurrency } from '@/lib/utils';

/**
 * Records one payment against an invoice.
 *
 * PART PAYMENT IS OFFERED (NEW-237). An earlier build deliberately withheld it,
 * on the reasoning that billing a job in stages is what partial *invoices* are
 * for — but a client can simply send a check that covers part of an invoice,
 * whichever kind it is, and the office then has nothing true to enter. So the
 * amount is an input, prefilled with the whole balance: settling in full stays
 * one click, and anything less lands the invoice in `partially_paid`, which the
 * ledger and the lifecycle rules have always derived correctly.
 *
 * AN UNPRICED INVOICE IS STILL PAYABLE. A backfilled QuickBooks row carries no
 * amount, and the office still receives money against it — refusing the payment
 * until someone reconstructs a figure from QuickBooks would be a dead end on a
 * record that is being phased out anyway. So the amount is typed freely there,
 * with nothing to check it against. (It previously submitted $0, which the
 * schema and the route both reject: the button could only ever fail.)
 *
 * OVERPAYMENT IS REFUSED, where there is something to overpay. `outstandingCents` floors at zero, so an excess would
 * vanish from every total rather than show up anywhere — and in practice a
 * figure above the balance is a typo. The cap is the balance; a client who
 * genuinely sends too much is a conversation, not a silent ledger row.
 *
 * Date, method and reference are kept (FR-1.1). An office that receives checks
 * needs the check number; dropping it would cost real information for no
 * simplification.
 */
interface MarkPaidDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  jobId?: string;
  invoiceNumber: string;
  /** What is still owed, in dollars. Prefills the amount and caps it. */
  outstanding: number;
  /**
   * True when the invoice's amount was never recorded — a backfilled row. The
   * amount is still asked for; it is simply uncapped and unprefilled, because
   * there is no balance to measure it against.
   */
  amountUnknown?: boolean;
  /**
   * Fired after the ledger write succeeds. Optional, and only callers outside
   * the job page pass it: useLedgerInvalidation already covers the payment,
   * invoice and job caches, but knows nothing about the /invoices workspace.
   */
  onRecorded?: () => void;
}

const todayLocal = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export function MarkPaidDialog({
  open,
  onOpenChange,
  invoiceId,
  jobId,
  invoiceNumber,
  outstanding,
  amountUnknown = false,
  onRecorded,
}: MarkPaidDialogProps) {
  const recordPayment = useRecordPayment(invoiceId, jobId);

  /**
   * The shared schema, with the amount's messages written for a person — an
   * empty number input parses as NaN, and "Expected number, received nan" is
   * not something to show an office — plus this invoice's ceiling.
   *
   * The ceiling is skipped on an unpriced row: a cap of $0 would reject every
   * payment, which is the dead end this dialog used to be. Comparison happens
   * in cents (NFR-2) so 0.1 + 0.2 can never read as more than a $0.30 balance.
   */
  const schema = useMemo(() => {
    const base = recordPaymentSchema.extend({
      amount: z
        .number({ invalid_type_error: 'Enter an amount.' })
        .positive('Enter an amount greater than zero.')
        .multipleOf(0.01, 'Amounts go to the cent.'),
    });
    return amountUnknown
      ? base
      : base.refine((d) => toCents(d.amount) <= toCents(outstanding), {
          path: ['amount'],
          message: `More than the ${formatCurrency(outstanding)} outstanding on this invoice.`,
        });
  }, [amountUnknown, outstanding]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RecordPaymentInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: amountUnknown ? undefined : outstanding,
      receivedOn: todayLocal(),
      method: 'cheque',
      reference: '',
    },
  });

  // The balance can move between opens — a reversal elsewhere, a refetch — so
  // the prefilled amount is re-read rather than captured at first mount.
  useEffect(() => {
    if (open) {
      reset({
        amount: amountUnknown ? undefined : outstanding,
        receivedOn: todayLocal(),
        method: 'cheque',
        reference: '',
      });
    }
  }, [open, outstanding, amountUnknown, reset]);

  const onSubmit = async (data: RecordPaymentInput) => {
    await recordPayment.mutateAsync({
      ...data,
      reference: data.reference?.trim() || null,
    });
    onRecorded?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* An unpriced invoice has no balance to describe, and saying so was
          noise. Radix warns about a content with no description unless the
          attribute is explicitly absent, hence the spread. */}
      <DialogContent {...(amountUnknown ? { 'aria-describedby': undefined } : {})}>
        <DialogHeader>
          <DialogTitle>Record payment — {invoiceNumber}</DialogTitle>
          {!amountUnknown && (
            <DialogDescription>
              {`${formatCurrency(outstanding)} outstanding. Recording less marks the invoice partially paid.`}
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
              Amount received
            </label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              {...register('amount', { valueAsNumber: true })}
            />
            {errors.amount && <p className="text-sm text-red-600 mt-1">{errors.amount.message}</p>}
          </div>

          <div>
            <label htmlFor="receivedOn" className="block text-sm font-medium text-gray-700 mb-1">
              Date received
            </label>
            <Input id="receivedOn" type="date" {...register('receivedOn')} />
            {errors.receivedOn && (
              <p className="text-sm text-red-600 mt-1">{errors.receivedOn.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-1">
              How was this paid?
            </label>
            {/* `external` is never offered — it marks migrated records, and must
                not be confusable with a cash INVOICE (FR-1.2). */}
            <select
              id="method"
              {...register('method')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SELECTABLE_PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
            {errors.method && <p className="text-sm text-red-600 mt-1">{errors.method.message}</p>}
          </div>

          <div>
            <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1">
              Reference <span className="text-gray-400">(optional)</span>
            </label>
            <Input
              id="reference"
              placeholder="Check number, transaction id…"
              {...register('reference')}
            />
            {errors.reference && (
              <p className="text-sm text-red-600 mt-1">{errors.reference.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={recordPayment.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={recordPayment.isPending}>
              {recordPayment.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Record payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
